import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button } from '@avangard/ui/core';
import { black, white } from '@avangard/ui/colors';
import { breakpoints } from '@avangard/ui/utils';

import { ShieldLogo } from '@modules/layout/icons';
import { LogoutIcon } from '@modules/layout/atoms';
import { useLogout } from '@modules/auth/hooks';

type OnlineApplicationLayoutProps = {
    children: React.ReactNode;
};

const OnlineApplicationLayout = (props: OnlineApplicationLayoutProps): React.ReactElement => {
    const { children } = props;

    const { t } = useTranslation(['common']);

    const { handleClickLogout, logoutLoading } = useLogout();

    return (
        <Root id='online-application-layout'>
            <Wrapper>
                <Header>
                    <Logo>
                        <ShieldLogo />
                    </Logo>

                    <LogoutButton
                        variant='text'
                        endIcon={<LogoutIcon />}
                        loading={logoutLoading}
                        onClick={handleClickLogout}
                    >
                        {t('common:actions.exit')}
                    </LogoutButton>
                </Header>

                <Content>{children}</Content>
            </Wrapper>
        </Root>
    );
};

const Root = styled.div`
    width: 100%;
    height: 100%;
    overflow: auto;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    min-height: 100%;
    background: ${white[50]};

    ${breakpoints.down('xs')} {
        min-height: calc(100vh);
    }
`;

const Header = styled.div`
    position: relative;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    align-self: flex-start;
    width: 100%;
    padding: 16px 24px;
    z-index: 3;
    background: ${white[100]};

    ${breakpoints.down('xs')} {
        padding: 16px 4px;
    }
`;

const LogoutButton = styled(Button)`
    margin-left: auto;
    color: ${black[100]};
`;

const Logo = styled.div`
    width: 100px;
    position: absolute;
    left: 50%;
    top: 0;
    transform: translateX(-50%);
`;

const Content = styled.div`
    width: 100%;
    margin: 80px auto 0;
    max-width: 700px;

    ${breakpoints.down('xs')} {
        margin: 60px auto 0;
    }
`;

export { OnlineApplicationLayout };
